import React, { useEffect, useRef, useState } from "react";
import { ensureUEditor } from "@utils/ueditorLoader";

interface UeditorProps {
  config?: any;
  value?: string;
  getEditor?: (ueditor: any) => void;
  [propName: string]: any;
}

let UEDITOR_ID_COUNTER = 0;

const Ueditor: React.FC<UeditorProps> = ({
  config,
  value,
  getEditor,
  style,
  className,
  ...rest
}) => {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const ueInstanceRef = useRef<any>(null);
  const [editorId] = useState(() => `ueditor_${UEDITOR_ID_COUNTER++}`);
  // 用于跟踪内部内容，避免外部 value 变化时重复设置导致光标跳转
  const internalContentRef = useRef<string>(value || "");

  const initializeEditor = () => {
    if (typeof window === "undefined") {
      return;
    }

    if (!window.UE || !window.UE.getEditor) {
      console.error("UEditor scripts are not ready.");
      return;
    }

    if (!editorRef.current) {
      console.error("UEditor container element is missing.");
      return;
    }

    try {
      const ueInstance = window.UE.getEditor(editorId, config);

      if (!ueInstance) {
        console.error("Failed to create UEditor instance.");
        return;
      }

      ueInstanceRef.current = ueInstance;

      if (typeof ueInstance.ready === "function") {
        ueInstance.ready(() => {
          if (
            value !== undefined &&
            typeof ueInstance.setContent === "function"
          ) {
            ueInstance.setContent(value);
            internalContentRef.current = value;
          }
          // 监听内容变化，同步更新内部引用
          if (typeof ueInstance.addListener === "function") {
            ueInstance.addListener("contentChange", () => {
              if (typeof ueInstance.getContent === "function") {
                internalContentRef.current = ueInstance.getContent();
              }
            });
          }
        });
      }

      if (typeof getEditor === "function") {
        getEditor(ueInstance);
      }

      console.log("UEditor initialized successfully.");
    } catch (error) {
      console.error("Unexpected error while initializing UEditor:", error);
    }
  };

  useEffect(() => {
    let unmounted = false;

    ensureUEditor()
      .then(() => {
        if (!unmounted) {
          initializeEditor();
        }
      })
      .catch((error) => {
        console.error("Failed to load UEditor assets:", error);
      });

    return () => {
      unmounted = true;
      if (
        ueInstanceRef.current &&
        typeof ueInstanceRef.current.destroy === "function"
      ) {
        try {
          ueInstanceRef.current.destroy();
        } catch (error) {
          console.warn("Error destroying UEditor instance:", error);
        }
      }
      ueInstanceRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (ueInstanceRef.current && value !== undefined) {
      // 只有当外部 value 与内部内容不同时才更新，避免用户输入时光标跳转
      if (value !== internalContentRef.current) {
        internalContentRef.current = value;
        const instance = ueInstanceRef.current;
        if (typeof instance.ready === "function") {
          instance.ready(() => {
            if (typeof instance.setContent === "function") {
              instance.setContent(value);
            }
          });
        }
      }
    }
  }, [value]);

  return (
    <div
      ref={editorRef}
      id={editorId}
      style={style}
      className={className}
      {...rest}
    />
  );
};

export default Ueditor;
